import { useRef, useState } from "react"
import type { ComponentPropsWithoutRef, SyntheticEvent } from "react"
import { t } from "@lingui/core/macro"
import { BlackClose } from "../../common/atoms/Icons/Chat"
import { sendEmoticonReport } from "../ReportMessage/api"
import { classNames } from "../utils"
import styles from "./EmoticonDetails.module.scss"
import type { IEmoticon } from "../RenderedMessage/utils"

interface EmoticonDetailsProps extends ComponentPropsWithoutRef<"div"> {
    emoticon: IEmoticon
    onClose: () => void
}

enum ReportState {
    default = "default",
    report = "report",
    undo = "undo",
}

export function EmoticonDetails({ emoticon, onClose }: EmoticonDetailsProps) {
    const [reportState, setReportState] = useState<ReportState>(
        ReportState.default,
    )
    const reportOptionsList = [
        { label: t`Just ignore this emoticon`, value: "ignore" },
        { label: t`Offensive`, value: "offensive" },
        { label: t`Disgusting`, value: "disgusting" },
        { label: t`Fake Tip`, value: "fake_tip" },
        { label: t`Advertising`, value: "advertising" },
    ]
    const selectRef = useRef<HTMLSelectElement>(null)
    const src = emoticon.imgUrl || emoticon.thumbUrl
    const reportOptions = reportOptionsList.map((option) => (
        <option key={option.value} value={option.value}>
            {option.label}
        </option>
    ))
    const report = () => {
        sendEmoticonReport(emoticon.name, selectRef.current?.value || "").then(
            () => {
                setReportState(ReportState.undo)
            },
        )
    }
    const undo = () => {
        sendEmoticonReport(emoticon.name, selectRef.current?.value || "").then(
            () => {
                setReportState(ReportState.default)
            },
        )
    }
    const absorb = (e: SyntheticEvent) => {
        e.stopPropagation()
    }
    return (
        <div className={styles.emoticonDetails} onClick={onClose}>
            <div className={styles.container} onClick={absorb}>
                <button
                    className={styles.closeX}
                    onClick={onClose}
                    data-testid="note-close-button"
                >
                    <BlackClose height={12} width={12} />
                </button>
                <img className={styles.image} alt={emoticon.name} src={src} />
                <button
                    className={classNames(styles.reportLink, {
                        [styles.hidden]: reportState !== ReportState.default,
                    })}
                    onClick={() => setReportState(ReportState.report)}
                >
                    {t`REPORT EMOTICON`}
                </button>
                <div
                    className={classNames(styles.reportWrapper, {
                        [styles.hidden]: reportState !== ReportState.report,
                    })}
                >
                    <div>
                        <span
                            className={styles.categoryLabel}
                        >{t`Choose a category:`}</span>
                        <select
                            className={styles.categorySelect}
                            ref={selectRef}
                        >
                            {reportOptions}
                        </select>
                    </div>
                    <div className={styles.buttonWrapper}>
                        <button
                            className={styles.cancelButton}
                            onClick={() => setReportState(ReportState.default)}
                        >
                            {t`CANCEL`}
                        </button>
                        <button
                            className={styles.reportButton}
                            onClick={() => report()}
                        >
                            {t`REPORT`}
                        </button>
                    </div>
                </div>
                <div
                    className={classNames(styles.undoWrapper, {
                        [styles.hidden]: reportState !== ReportState.undo,
                    })}
                >
                    <span>{t`EMOTICON REPORTED - `}</span>
                    <button
                        className={styles.undoButton}
                        onClick={() => undo()}
                    >
                        {t`UNDO`}
                    </button>
                </div>
            </div>
        </div>
    )
}
